import { parse, isValid } from 'date-fns';
import { performGetRequest } from '@atlassian/jira-fetch/src/index.tsx';
import { formatWithLocale } from '@atlassian/jira-platform-utils-date-fns/src/main.tsx';

const JSM_CONTEXT_URL = '/rest/servicedesk/latest/jsmcontext';
const getSubscriptionsUrl = (entitlementId: string): string =>
	`/gateway/api/ccp/api/v2/entitlements/${entitlementId}/details`;

// The Packaging GP enforcement date is October 16, 2025.
const PACKAGING_GP_ENFORCEMENT_DATE = new Date('2025-10-16');

type BillingPeriod = 'MONTHLY' | 'ANNUAL';

type GpEndDateDetails = {
	maintenanceEndDate: Date;
	billingPeriod: BillingPeriod;
};

export interface GpEndDateData {
	gpEndDateDetails: GpEndDateDetails;
	gracePeriodEndDate: Date | undefined;
}

const dateHasExpectedFormat = (date: string) => {
	const dateFormat = 'yyyy-MM-dd';
	const parsedDate = parse(date, dateFormat, new Date());

	return isValid(parsedDate);
};

const getSubscriptionsDateDetails = async (entitlementId: string) => {
	const data = await performGetRequest(getSubscriptionsUrl(entitlementId));
	return data.subscriptionv2.endTimestamp;
};

const getJsmContextDateDetails = async (): Promise<{
	maintenanceEndDate: Date | undefined;
	billingPeriod: BillingPeriod;
}> => {
	const data = await performGetRequest(JSM_CONTEXT_URL);

	if (data.maintenanceEndDate && !dateHasExpectedFormat(data.maintenanceEndDate)) {
		throw new Error(`Invalid maintenance end date, received: ${data.maintenanceEndDate}`);
	}

	if (data.billingPeriod !== 'MONTHLY' && data.billingPeriod !== 'ANNUAL') {
		throw new Error(`Invalid billing period, received: ${data.billingPeriod}`);
	}

	return {
		// Users with termed subscriptions before Aug 2024 will have no maintenance end date in the JSM context.
		maintenanceEndDate: data.maintenanceEndDate ? new Date(data.maintenanceEndDate) : undefined,
		billingPeriod: data.billingPeriod,
	};
};

export const getGpEndDateDetails = async (
	isAdmin: boolean,
	entitlementId: string | null | undefined,
): Promise<GpEndDateDetails> => {
	if (!isAdmin) {
		throw new Error('Insufficient permissions to access GP end date');
	}

	// Try the primary method first
	const jsmContextDateDetails = await getJsmContextDateDetails();

	if (jsmContextDateDetails.maintenanceEndDate) {
		return {
			maintenanceEndDate: jsmContextDateDetails.maintenanceEndDate,
			billingPeriod: jsmContextDateDetails.billingPeriod,
		};
	}

	if (!entitlementId) {
		throw new Error('Insufficient data to access GP end date');
	}

	// If primary method fails, try the fallback. This is for users with termed subscriptions before Aug 2024.
	const endTimestamp = await getSubscriptionsDateDetails(entitlementId);

	if (!endTimestamp || !Number.isInteger(endTimestamp)) {
		throw new Error(`Invalid end timestamp from subscriptions, received: ${endTimestamp}`);
	}

	return {
		// Convert from seconds to milliseconds. endTimestamp is a UNIX timestamp.
		maintenanceEndDate: new Date(endTimestamp * 1000),
		billingPeriod: jsmContextDateDetails.billingPeriod,
	};
};

const getMonthlyBillingGPEndDate = (date: Date): Date => {
	const currentBillingDate = date;
	while (currentBillingDate < PACKAGING_GP_ENFORCEMENT_DATE) {
		currentBillingDate.setMonth(currentBillingDate.getMonth() + 1);
	}
	return currentBillingDate;
};

const getAnnualBillingGPEndDate = (date: Date): Date => {
	const currentBillingDate = date;
	while (currentBillingDate < PACKAGING_GP_ENFORCEMENT_DATE) {
		currentBillingDate.setFullYear(currentBillingDate.getFullYear() + 1);
	}
	return currentBillingDate;
};

export const calculateGpEndDate = (
	maintenanceEndDate: Date,
	billingPeriod: BillingPeriod,
	locale: string,
) => {
	const gpEndDate = calculateGpEndDateWithoutFormat(maintenanceEndDate, billingPeriod);

	return gpEndDate ? formatWithLocale(gpEndDate, 'MMM dd, yyyy', locale) : undefined;
};

export const calculateGpEndDateWithoutFormat = (
	maintenanceEndDate: Date,
	billingPeriod: BillingPeriod,
) => {
	if (billingPeriod === 'MONTHLY') {
		return getMonthlyBillingGPEndDate(maintenanceEndDate);
	}

	if (billingPeriod === 'ANNUAL') {
		return getAnnualBillingGPEndDate(maintenanceEndDate);
	}
};
